import { useEffect, useRef } from "react";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";
import { postLocationSearch } from "../../../queries/locations";
import { geocode } from "../../mapping/queries/maps";

interface LocationsGeocodingOptions {
  onPlaceSelected: (place: {
    address: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
    countryId: string;
    apn?: string;
    location?: { x: number; y: number };
    attributes: Record<string, any>;
  }) => void;
  getCoords?: boolean;
}

/**
 * Custom hook for internal locations autocomplete
 */
export const useLocationsWidget = ({ onPlaceSelected, getCoords }: LocationsGeocodingOptions) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const suggestionsRef = useRef<HTMLDivElement | null>(null);
  const suggestionsListRef = useRef<any[]>([]);
  const selectedIndexRef = useRef<number>(-1);
  const networkHeaders = useNetworkHeaders();
  const searchFn = postLocationSearch(networkHeaders);

  const setupAutocomplete = () => {
    const input = inputRef.current;
    if (!input) return;

    if (!suggestionsRef.current) {
      const suggestionsDiv = document.createElement("div");
      suggestionsDiv.className = "locations-suggestions";
      suggestionsDiv.style.position = "absolute";
      suggestionsDiv.style.zIndex = "1000";
      suggestionsDiv.style.backgroundColor = "white";
      suggestionsDiv.style.border = "1px solid #ccc";
      suggestionsDiv.style.width = `${input.offsetWidth}px`;
      suggestionsDiv.style.maxHeight = "200px";
      suggestionsDiv.style.overflowY = "auto";
      suggestionsDiv.style.display = "none";

      input.parentNode?.insertBefore(suggestionsDiv, input.nextSibling);
      suggestionsRef.current = suggestionsDiv;
    }

    const updateSelectedItem = (newIndex: number) => {
      if (!suggestionsRef.current) return;

      const items = suggestionsRef.current.querySelectorAll(".locations-suggestion-item");

      // Remove highlight from all items
      items.forEach((item) => {
        (item as HTMLElement).style.backgroundColor = "white";
      });

      // Highlight new selected item
      if (newIndex >= 0 && newIndex < items.length) {
        const selectedItem = items[newIndex] as HTMLElement;
        selectedItem.style.backgroundColor = "#e6f3ff";

        // Scroll item into view if needed
        selectedItem.scrollIntoView({ block: "nearest" });
      }

      selectedIndexRef.current = newIndex;
    };

    const selectCurrentItem = async () => {
      const selectedIndex = selectedIndexRef.current;
      if (selectedIndex >= 0 && selectedIndex < suggestionsListRef.current.length) {
        const location = suggestionsListRef.current[selectedIndex];
        const locationAddress = location._source.location_address;
        console.log(`Selected source:`, location._source);

        if (!locationAddress) return;

        // Set the input value to the full address
        const fullAddress = [locationAddress.address, locationAddress.city, locationAddress.state, locationAddress.zip]
          .filter(Boolean)
          .join(", ");

        if (inputRef.current) inputRef.current.value = fullAddress;

        // Geocode the address to get coordinates
        const coordinates = await (async () => {
          try {
            if (!getCoords) return undefined;
            const geocodeResults = await geocode(fullAddress);
            if (!geocodeResults || !geocodeResults.length) return undefined;
            const location = geocodeResults[0].location;
            if (!location) return undefined;
            const { longitude, latitude } = location;
            if (!longitude || !latitude) return undefined;
            return { x: longitude, y: latitude };
          } catch (error) {
            console.log(`Failed to geocode address: ${fullAddress}`, error);
            return undefined;
          }
        })();

        onPlaceSelected({
          address: locationAddress.address || "",
          address2: locationAddress.address2 || "",
          city: locationAddress.city || "",
          state: locationAddress.state || "",
          zip: locationAddress.zip || "",
          countryId: locationAddress.country_id || "US",
          location: coordinates,
          apn: location._source.parcel_number || "",
          attributes: location._source || {},
        });

        if (suggestionsRef.current) {
          suggestionsRef.current.style.display = "none";
        }
        selectedIndexRef.current = -1;
      }
    };

    let debounceTimer: number;

    const handleInputChange = (e: Event) => {
      const inputElement = e.target as HTMLInputElement;
      const inputValue = inputElement.value;

      // Reset selection when typing
      selectedIndexRef.current = -1;

      if (inputValue.length < 3) {
        if (suggestionsRef.current) suggestionsRef.current.style.display = "none";
        return;
      }

      // Debounce the search
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(async () => {
        try {
          const results = await searchFn({
            take: 10,
            query: inputValue,
            queryFields: [
              "location_name",
              "location_address.attn",
              "location_address.address",
              "location_address.address2",
              "location_address.city",
              "location_address.state",
              "location_address.zip",
              "parcel_number",
              "owner_name",
            ],
            sort: [],
            includeDeactivated: false,
          });

          const locations = results?.hits?.hits || [];
          suggestionsListRef.current = locations;
          displaySuggestions(locations);
        } catch (error) {
          console.error("Location search failed:", error);
          if (suggestionsRef.current) suggestionsRef.current.style.display = "none";
        }
      }, 300);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!suggestionsRef.current || suggestionsRef.current.style.display === "none") {
        return;
      }

      const suggestionsCount = suggestionsListRef.current.length;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          const nextIndex = selectedIndexRef.current < suggestionsCount - 1 ? selectedIndexRef.current + 1 : 0;
          updateSelectedItem(nextIndex);
          break;

        case "ArrowUp":
          e.preventDefault();
          const prevIndex = selectedIndexRef.current > 0 ? selectedIndexRef.current - 1 : suggestionsCount - 1;
          updateSelectedItem(prevIndex);
          break;

        case "Enter":
          e.preventDefault();
          if (selectedIndexRef.current >= 0) {
            selectCurrentItem();
          }
          break;

        case "Escape":
          e.preventDefault();
          if (suggestionsRef.current) {
            suggestionsRef.current.style.display = "none";
          }
          selectedIndexRef.current = -1;
          break;
      }
    };

    const displaySuggestions = (locations: any[]) => {
      if (!suggestionsRef.current) return;
      suggestionsRef.current.innerHTML = "";
      selectedIndexRef.current = -1;

      if (locations.length === 0) {
        const noResults = document.createElement("div");
        noResults.className = "locations-suggestion-item";
        noResults.style.padding = "8px 12px";
        noResults.style.color = "#999";
        noResults.textContent = "No locations found";
        suggestionsRef.current.appendChild(noResults);
        suggestionsRef.current.style.display = "block";
        return;
      }

      locations.forEach((location, index) => {
        const item = document.createElement("div");
        item.className = "locations-suggestion-item";
        item.style.padding = "8px 12px";
        item.style.cursor = "pointer";
        item.style.borderBottom = index < locations.length - 1 ? "1px solid #eee" : "none";

        const locationData = location._source;
        const locationAddress = locationData.location_address;

        // Format the display text
        const addressParts = [
          locationData.location_name,
          locationAddress?.address,
          locationAddress?.city,
          locationAddress?.state,
          locationAddress?.zip,
        ].filter(Boolean);

        item.textContent = addressParts.join(", ");

        item.addEventListener("mouseenter", () => {
          updateSelectedItem(index);
        });

        item.addEventListener("mouseleave", () => {
          if (selectedIndexRef.current === index) {
            item.style.backgroundColor = "white";
            selectedIndexRef.current = -1;
          }
        });

        item.addEventListener("mousedown", (e) => {
          e.preventDefault();
        });

        item.addEventListener("mouseup", async (e) => {
          e.preventDefault();
          selectedIndexRef.current = index;
          await selectCurrentItem();
        });

        suggestionsRef.current?.appendChild(item);
      });

      suggestionsRef.current.style.display = "block";
      suggestionsRef.current.style.width = `${inputRef.current!.offsetWidth}px`;
      suggestionsRef.current.style.top = `${inputRef.current!.offsetTop + inputRef.current!.offsetHeight}px`;
      suggestionsRef.current.style.left = `${inputRef.current!.offsetLeft}px`;
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        e.target !== suggestionsRef.current &&
        e.target !== inputRef.current &&
        !suggestionsRef.current.contains(e.target as Node)
      ) {
        suggestionsRef.current.style.display = "none";
        selectedIndexRef.current = -1;
      }
    };

    input.addEventListener("input", handleInputChange);
    input.addEventListener("keydown", handleKeyDown);
    document.addEventListener("click", handleClickOutside);

    return () => {
      clearTimeout(debounceTimer);
      input.removeEventListener("input", handleInputChange);
      input.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("click", handleClickOutside);

      if (suggestionsRef.current && suggestionsRef.current.parentNode) {
        suggestionsRef.current.parentNode.removeChild(suggestionsRef.current);
        suggestionsRef.current = null;
      }
    };
  };

  useEffect(() => {
    return setupAutocomplete();
  }, []);

  return { ref: inputRef };
};

export default useLocationsWidget;
